import { z } from "zod";
import { locales } from "@/config";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { fieldError } from "./errors";


/* form schema builder
 * -------------------------------------------------------------------------- */

export { createBuilder, mergeBuild } from "./builder";


/* helpers
 * -------------------------------------------------------------------------- */

export type Values<T extends z.ZodType> = z.output<T>;

export function form<TShape extends z.ZodRawShape>(shape: TShape) {
  return z.object(shape);
}

export function enumTs<const TValues extends [string, ...string[]]>(values: TValues) {
  return z.enum(values, { message: fieldError.invalidValue });
}

export function boolTs<TDefault extends boolean>(defaultValue: TDefault) {
  return z.boolean().default(defaultValue);
}


/* general
 * -------------------------------------------------------------------------- */

export const localeTs = z.enum(locales);


/* contact infos
 * -------------------------------------------------------------------------- */

export const fullNameTs = z
  .string()
  .min(1, fieldError.required)
  .min(2, fieldError.invalidValue)
  .max(50, fieldError.invalidValue);


export const emailTs = z
  .email(fieldError.invalidFormat)
  .max(100, fieldError.invalidValue);


export const companyTs = z
  .string()
  .min(1, fieldError.required)
  .min(2, fieldError.invalidValue)
  .max(50, fieldError.invalidValue);


export type Phone = `+${number}.${number}`;

export const phoneTs = z
  .string()
  .regex(/^\+\d+\.\d+$/, fieldError.invalidFormat)
  .transform(phone => phone as Phone)
  .refine(isPhoneValid, fieldError.invalidValue);

function isPhoneValid(phone: Phone) {
  const [prefix, localNumber] = phone.split(".");
  const fullNumber = `${prefix}${localNumber}`;

  const parsed = parsePhoneNumberFromString(fullNumber);
  return parsed?.isValid() ?? false;
}


/* address
 * -------------------------------------------------------------------------- */

export const addressStreetTs = z
  .string()
  .min(1, fieldError.required)
  .max(50, fieldError.invalidValue);


export const addressNumberTs = z
  .string()
  .min(1, fieldError.required)
  .max(20, fieldError.invalidValue);


export const addressExtraTs = z
  .string()
  .max(20, fieldError.invalidValue);


export const addressZipTs = z
  .string()
  .min(1, fieldError.required)
  .min(4, fieldError.invalidValue)
  .max(12, fieldError.invalidValue)
  .regex(/^\d+$/, fieldError.invalidFormat);


export const addressCityTs = z
  .string()
  .min(1, fieldError.required)
  .max(80, fieldError.invalidValue);


/* tax id
 * -------------------------------------------------------------------------- */

export const retailerCountryTs = z.enum([
  "BE", "FR",
  //"UK", "ES",
  //"_other",
], {
  message: fieldError.invalidValue,
});


export const retailerStoreNameTs = z
  .string()
  .min(1, fieldError.required)
  .max(60, fieldError.invalidValue);


export const genericTaxId = z
  .string()
  .min(1, fieldError.required)
  .min(9, fieldError.invalidFormat)
  .max(20, fieldError.invalidValue);


export const vatBeTs = z
  .string()
  .min(12, fieldError.invalidFormat)
  .max(20, fieldError.invalidFormat)
  .regex(/^[\dA-Za-z\s]+$/, fieldError.invalidFormat)
  .transform(val => val.replace(/\s+/g, "").toUpperCase())
  .refine(isValidVatBe, fieldError.invalidFormat);

function isValidVatBe(raw: string): boolean {
  const vat = raw.replace(/\s+/g, "").toUpperCase();
  return /^BE\d{10}$/.test(vat);
}


export const siretTs = z
  .string()
  .min(14, fieldError.invalidFormat)
  .max(20, fieldError.invalidFormat)
  .regex(/^[\d\s]+$/, fieldError.invalidFormat)
  .transform(val => val.replace(/\s+/g, ""))
  .refine(isValidSiret, fieldError.invalidFormat);

function isValidSiret(raw: string): boolean {
  const siret = raw.replace(/\s+/g, "");

  if (!/^\d{14}$/.test(siret)) {
    return false;
  }

  let sum = 0;

  for (let i = 0; i < 14; i++) {
    let digit = parseInt(siret.charAt(i), 10);
    if (i % 2 === 0) digit = digit * 2;
    if (digit > 9) digit = digit - 9;
    sum += digit;
  }

  return sum % 10 === 0;
}


/* visibility kit
 * -------------------------------------------------------------------------- */

export const kitLeafletQtyTs = z.enum([
  "_0",
  "_10",
  "_25",
  "_50",
]).default("_0");

export const kitStickerQtyTs = z.enum([
  "_0",
  "_1",
  "_2",
  "_5",
]).default("_0");


export const OptionalMessageTs = z.string().nullable().optional()
